import React, { useState, useEffect } from 'react';
import styles from './BrandDashboard.module.css';
import FeatureHeader from '../components/FeatureHeader';
import { formatINR } from '../utils/currency';
import { classifyError } from '../utils/errorTaxonomy';
import DataFallback from '../components/feedback/DataFallback';
import { setKpiDataGlobally } from '../components/GlobalData';
import Tooltip from '../components/Tooltip';

// --- Helper Components & SVGs ---
const StarIcon = ({ filled }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${styles.starIcon} ${filled ? styles.starFilled : styles.starEmpty}`}>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);

const Card = ({ children, title, action, className = '' }) => (
    <div className={`${styles.card} ${className}`}>
        <div className={styles.cardHeader}>
            <h3>{title}</h3>
            {action && <div>{action}</div>}
        </div>
        <div className={styles.cardBody}>
            {children}
        </div>
    </div>
);

const MetricItem = ({ label, value, valueColorClass = '', valueSizeClass = 'metricItemValue', tooltipContent = '', tooltipPosition = 'top' }) => (
    // display in same line
    <div className={styles.valueListItem}>
        <div className={styles.metricItemLabel}>
            {tooltipContent ? (
                <Tooltip content={tooltipContent} position={tooltipPosition}>
                    <span>{label}</span>
                </Tooltip>
            ) : (
                label
            )}
        </div>
        <p className={`${styles[valueSizeClass]} ${styles[valueColorClass]}`} style={{ fontWeight: 700 }}>{value}</p>
    </div>
);

const LinkItem = ({ label, value, actionLabel, onActionClick }) => (
    <div className={styles.linkItem}>
        <div>
            <p className={styles.metricItemLabel}>{label}</p>
            <p className={styles.metricItemValue} style={{ fontSize: '1.25rem' }}>{value}</p>
        </div>
        <button onClick={onActionClick} className={styles.linkItemButton}>{actionLabel}</button>
    </div>
);

// --- Main Dashboard Sections ---
const BrandSnapshot = ({ data, loading }) => {
    const snap = data?.brand_snapshot || {};
    const avgRating = Number(snap?.average_rating ?? 0);
    const filledStars = Math.max(0, Math.min(5, Math.round(avgRating)));

    return (
        <Card title="Brand Snapshot">
            <div className={styles.valueList} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem' }}>
                <MetricItem label="Gross Sales" value={loading ? "Loading..." : <span style={{ fontWeight: 800, color: '#0f766e' }}>{formatINR(data?.gross_sales, { round: 'ceil' })}</span>} tooltipContent="The total revenue generated from all sales before any deductions like discounts and returns." tooltipPosition="top-start" />
                <MetricItem label="New Customers" value={loading ? "Loading..." : (data?.new_customers ?? '—')} tooltipContent="Number of new customers who placed an order." />
                <MetricItem label="Discounts" value={loading ? "Loading..." : <span style={{ fontWeight: 800, color: '#0f766e' }}>{formatINR(data?.discounts, { round: 'ceil' })}</span>} tooltipContent="Account discounted from sales revenue." tooltipPosition="top-start" />
                <MetricItem label="Returning Customer Rate" value={loading ? "Loading..." : (data?.returning_customer_rate ? `${(data.returning_customer_rate * 100).toFixed(2)}%` : '—')} tooltipContent="Percentage of returning customers relative to all customers who placed orders." />
                <MetricItem label="Total Quantity Ordered" value={loading ? "Loading..." : (data?.quantity_ordered ?? '—')} tooltipContent="Number of items ordered, before any returns." tooltipPosition="top-start" />
                <MetricItem label="Number of Orders per Customer" value={loading ? "Loading..." : (data?.number_of_orders_per_customer ?? '—')} tooltipContent="The average number of orders placed by each customer." />
                <MetricItem label="Average Quantity Ordered per Order" value={loading ? "Loading..." : (data?.quantity_ordered_per_order ?? '—')} tooltipContent="The mean number of items per order." tooltipPosition="top-start" />
                <MetricItem label="Predicted Customer Lifetime Value" value={loading ? "Loading..." : <span style={{ fontWeight: 800, color: '#0f766e' }}>{formatINR((data?.number_of_orders_per_customer || 0) * (data?.average_order_value || 0), { round: 'ceil' })}</span>} tooltipContent="An estimated total revenue a customer is expected to generate over their entire relationship with the business, based on historical data and predictive analytics." />
                <MetricItem label="Orders" value={loading ? "Loading..." : (data?.number_of_orders ?? '—')} tooltipContent="Number of orders placed." tooltipPosition="top-start" />
                <MetricItem label="Global Search Requests" value={loading ? "Loading..." : (data?.global_search_requests ?? '—')} tooltipContent="Number of unqiue global search requests." />
                <MetricItem label="Customers (Total Customers)" value={loading ? "Loading..." : (data?.unique_customer_count ?? '—')} tooltipContent="The total count of unique individuals who have placed at least one order." tooltipPosition="top-start" />
                <MetricItem label="Product Recommendation Click Rate" value={loading ? "Loading..." : (data?.product_recommendation_click_rate ? `${(data.product_recommendation_click_rate * 100).toFixed(2)}%` : '—')} tooltipContent="Percentage of sessions with product recommendations where customers clicked on at least one recommendation." />
            </div>
        </Card>
    );
};

const ProductCarouselInline = ({ items = 3, variant = 'cards' }) => {
    if (variant === 'compact') {
        const labels = Array.isArray(items)
            ? items
            : [...Array(items)].map((_, i) => `Product ${i + 1}`);
        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(120px, 1fr))',
                gap: '0.75rem',
                alignItems: 'center',
                width: '100%'
            }}>
                {labels.map((label, idx) => (
                    <div key={idx} style={{
                        padding: '0.5rem 0.8rem',
                        borderRadius: 10,
                        background: 'rgba(148,163,184,0.15)',
                        color: '#475569',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        textAlign: 'center',
                        width: '100%'
                    }}>{label}</div>
                ))}
            </div>
        );
    }
    return (
        <div className={styles.productCarouselContainer}>
            {[...Array(items)].map((_, i) => (
                <div key={i} className={styles.productCarouselItem}><p>Product {i + 1}</p></div>
            ))}
        </div>
    );
};

const TopPerformerProductCard = ({ product, idx }) => {
    // Build array of valid images (up to 3)
    const images = [];
    if (product.product_image_1 && product.product_image_1 !== '') {
        const src1 = String(product.product_image_1).startsWith('http')
            ? product.product_image_1
            : `${process.env.REACT_APP_API_BASE_URL}${product.product_image_1}`;
        images.push(src1);
    }
    if (product.product_image_2 && product.product_image_2 !== '') {
        const src2 = String(product.product_image_2).startsWith('http')
            ? product.product_image_2
            : `${process.env.REACT_APP_API_BASE_URL}${product.product_image_2}`;
        images.push(src2);
    }
    if (product.product_image_3 && product.product_image_3 !== '') {
        const src3 = String(product.product_image_3).startsWith('http')
            ? product.product_image_3
            : `${process.env.REACT_APP_API_BASE_URL}${product.product_image_3}`;
        images.push(src3);
    }

    // Inner carousel state for product images
    const [imgIndex, setImgIndex] = React.useState(0);

    // Auto-cycle through images every 3 seconds
    React.useEffect(() => {
        setImgIndex(0); // Reset image index when product changes
    }, [idx]);

    React.useEffect(() => {
        if (images.length > 1) {
            const interval = setInterval(() => {
                setImgIndex((prevIndex) => (prevIndex + 1) % images.length);
            }, 3000); // Auto-cycle every 3 seconds

            return () => clearInterval(interval); // Cleanup on unmount
        }
    }, [images.length]);

    const hasMultipleImages = images.length > 1;
    const currentImageSrc = images.length > 0 ? images[imgIndex] : null;

    return (
        <div
            className={styles.carouselCard}
            style={{ flex: '0 0 100%', minWidth: '100%' }}
        >
            <div
                className={styles.carouselImage}
                style={{
                    backgroundImage: currentImageSrc ? `url(${currentImageSrc})` : 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
                    backgroundSize: currentImageSrc ? 'cover' : 'auto',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}
                aria-label={product.product_name || product.title || 'Product'}
                role="img"
            >
                {!currentImageSrc && (
                    <div style={{ fontSize: '2rem', color: '#64748b' }}>
                        🏆
                    </div>
                )}
                {/* Rank badge - top-right corner */}
                {product.rank && (
                    <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#0d9488',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                    }}>
                        #{product.rank}
                    </div>
                )}
                {/* Dot indicators at the bottom - only show if multiple images */}
                {hasMultipleImages && (
                    <div style={{
                        position: 'absolute',
                        bottom: '0.75rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '8px',
                        padding: '8px 12px',
                        background: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: '12px',
                        backdropFilter: 'blur(8px)'
                    }}>
                        {images.map((_, dotIdx) => (
                            <button
                                key={dotIdx}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setImgIndex(dotIdx); }}
                                style={{
                                    width: imgIndex === dotIdx ? '10px' : '8px',
                                    height: imgIndex === dotIdx ? '10px' : '8px',
                                    borderRadius: '50%',
                                    border: 'none',
                                    backgroundColor: imgIndex === dotIdx ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
                                    cursor: 'pointer',
                                    padding: 0,
                                    transition: 'all 0.2s ease'
                                }}
                                aria-label={`Go to image ${dotIdx + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
            <div className={styles.carouselBody}>
                <div className={styles.carouselTitle} style={{ color: '#1e293b', fontWeight: 700 }}>
                    {product.product_name || product.title || 'Top Product'}
                </div>
                <div className={styles.carouselSubtitle} style={{ color: '#64748b' }}>
                    {product.brand_name || 'Brand'}
                </div>
            </div>
        </div>
    );
};

const TopPerformerCarousel = ({ items = [] }) => {
    const list = Array.isArray(items) ? items : [];
    const [index, setIndex] = React.useState(0);
    const trackRef = React.useRef(null);

    // Reset index if list changes/shortens
    React.useEffect(() => {
        if (index >= list.length) setIndex(0);
    }, [list.length, index]);

    if (!list.length) {
        // Empty state placeholder
        return (
            <div className={styles.carouselContainer}>
                <div className={styles.carouselViewport}>
                    <div className={styles.carouselTrack}>
                        <div className={styles.carouselCard}>
                            <div className={styles.carouselImage} style={{ background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '3rem', color: '#0d9488' }}>
                                    🏆
                                </div>
                            </div>
                            <div className={styles.carouselBody}>
                                <div className={styles.carouselTitle} style={{ color: '#1e293b', fontWeight: 700 }}>
                                    No top performers added yet
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const prev = () => setIndex((i) => (i - 1 + list.length) % list.length);
    const next = () => setIndex((i) => (i + 1) % list.length);

    return (
        <div className={styles.carouselContainer}>
            <button
                type="button"
                className={`${styles.carouselNav} ${styles.carouselNavLeft}`}
                onClick={prev}
                aria-label="Previous"
            >
                ‹
            </button>

            <div className={styles.carouselViewport}>
                <div
                    className={styles.carouselTrack}
                    ref={trackRef}
                    style={{
                        display: 'flex',
                        gap: 0,
                        transition: 'transform 0.4s ease',
                        transform: `translateX(-${index * 100}%)`,
                        willChange: 'transform',
                        width: '100%',
                    }}
                >
                    {list.map((p, idx) => (
                        <TopPerformerProductCard key={idx} product={p} idx={idx} />
                    ))}
                </div>
            </div>

            <button
                type="button"
                className={`${styles.carouselNav} ${styles.carouselNavRight}`}
                onClick={next}
                aria-label="Next"
            >
                ›
            </button>
        </div>
    );
};
const LiveListingsHealth = ({ data, loading }) => {
    return (
        <Card title="Live Listings Health">
            <div className={styles.valueList}>
                <div className={styles.valueListItem}>
                    <p className={styles.metricItemLabel}>
                        <Tooltip content="The count of direct-to-consumer products currently available and active on the platform." showInfoIcon={true} position="top-start">
                            <span>No. of D2C Products Live</span>
                        </Tooltip>
                    </p>
                    <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>
                        {loading ? "Loading..." : (data?.brand_snapshot?.no_of_d2c_products_live ?? '—')}
                    </p>
                </div>
                <div className={styles.valueListItem}>
                    <p className={styles.metricItemLabel}>
                        <Tooltip content="The count of B2B products currently available and active on the platform." showInfoIcon={true} position="top-start">
                            <span>No. of B2B Products Live</span>
                        </Tooltip>
                    </p>
                    <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>
                        {loading ? "Loading..." : (data?.brand_snapshot?.no_of_b2b_products_live ?? '—')}
                    </p>
                </div>
                <div className={styles.valueListItem}>
                    <p className={styles.metricItemLabel}>
                        <Tooltip content="The cumulative amount customers have saved through discounts, promotions, or other cost reductions." position="top-start">
                            <span>Total Money Saved</span>
                        </Tooltip>
                    </p>
                    <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>
                        {loading ? "Loading..." : <span style={{ fontWeight: 800, color: '#0f766e' }}>{formatINR(data?.brand_snapshot?.total_money_saved, { round: 'ceil' })}</span>}
                    </p>
                </div>
            </div>
        </Card>
    );
};

const VisibilityTracker = ({ data }) => {
    const snap = data?.brand_snapshot || {};
    return (
        <Card title="Visibility Tracker">
            <div className={styles.valueList}>
                <div className={styles.valueListItem}>
                    <p className={styles.metricItemLabel}>
                        <Tooltip content="The number of times user profiles were viewed as a result of marketing campaigns." position="top-start">
                            <span>Total Profile Visits via Campaigns</span>
                        </Tooltip>
                    </p>
                    <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>{snap?.profile_visits ?? '—'}</p>
                </div>
                <div className={styles.valueListItem}>
                    <p className={styles.metricItemLabel}>
                        <Tooltip content="The total number of times paid advertisements were displayed to users." position="top-start">
                            <span>Total Paid Impressions</span>
                        </Tooltip>
                    </p>
                    <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>{data?.total_paid_impressions ?? '—'}</p>
                </div>
                <div className={styles.valueListItem}>
                    <p className={styles.metricItemLabel}>
                        <Tooltip content="The total number of unique users reached through marketing efforts." position="top-start">
                            <span>Total Unique Marketing Reach</span>
                        </Tooltip>
                    </p>
                    <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>{data?.total_unique_marketing_reach ?? '—'}</p>
                </div>
            </div>
        </Card>
    );
};

const LogisticsTracker = () => {
    const [month, setMonth] = useState('August');
    return (
        <Card title="Logistics Tracker" action={
            <select value={month} onChange={(e) => setMonth(e.target.value)} className={styles.dropdown}>
                <option>August</option> <option>July</option> <option>June</option>
            </select>
        }>
            <div className={styles.valueList}>
                <div className={styles.valueListItem}><p className={styles.metricItemLabel}>Orders Shipped</p><p className={`${styles.metricItemValue} ${styles.metricItemValue}`} style={{ fontSize: '1.25rem' }}>XX</p></div>
                <div className={styles.valueListItem}><p className={styles.metricItemLabel}>Orders In Transit</p><p className={`${styles.metricItemValue} ${styles.metricItemValue}`} style={{ fontSize: '1.25rem' }}>XX</p></div>
                <div className={styles.valueListItem}><p className={styles.metricItemLabel}>Orders Returned</p><p className={`${styles.metricItemValue} ${styles.metricItemValue}`} style={{ fontSize: '1.25rem' }}>XX</p></div>
            </div>
        </Card>
    );
};

const CommunitySnapshot = ({ data, loading }) => {
    const top = Array.isArray(data?.top_performer_on_suspire) ? data.top_performer_on_suspire : [];
    const items = top.length ? top.map((p, i) => p?.title || p?.name || `Product ${i + 1}`) : ["Product 1", "Product 2", "Product 3"];
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '1.5rem', alignItems: 'stretch' }}>
            <Card title="Community Snapshot">
                <div className={styles.valueList}>
                    <div className={styles.valueListItem}>
                        <p className={styles.metricItemLabel}>
                            <Tooltip content="The total number of brands registered or active on the platform." position="top-start">
                                <span>No. of Brands on Suspire</span>
                            </Tooltip>
                        </p>
                        <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>{loading ? "Loading..." : "400+"}</p>
                    </div>
                    <div className={styles.valueListItem}>
                        <p className={styles.metricItemLabel}>
                            <Tooltip content="The count of brands that have been verified and certified under the 3S program." position="top-start">
                                <span>No. of Verified 3S Certified Brands</span>
                            </Tooltip>
                        </p>
                        <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>{loading ? "Loading..." : (data?.no_of_verified_3s_certified_brands ?? '—')}</p>
                    </div>
                    <div className={styles.valueListItem}>
                        <p className={styles.metricItemLabel}>
                            <Tooltip content="Total number of conversations with TARA across Suspire Partner Brands" position="top-start">
                                <span>Total TARA conversation</span>
                            </Tooltip>
                        </p>
                        <p className={`${styles.metricItemValue} ${styles.metricItemValue}`} style={{ textAlign: 'left' }}>{loading ? "Loading..." : (data?.total_tara_messages ?? 0)}</p>
                    </div>
                    <div className={styles.valueListItem}>
                        <p className={styles.metricItemLabel}>
                            <Tooltip content="The number of vendors integrated or connected via SSN." position="top-start">
                                <span>No. of vendors connected through SSN</span>
                            </Tooltip>
                        </p>
                        <p className={`${styles.metricItemValue} ${styles.metricItemValue}`}>{loading ? "Loading..." : (data?.no_of_vendors_connected_through_ssn ?? '—')}</p>
                    </div>
                </div>
            </Card>
            <Card title="Top Performer on Suspire" className={styles.topPerformerCard}>
                <TopPerformerCarousel items={top} />
            </Card>
        </div>
    );
};

const OnboardingBannerPlaceholder = ({ bannerImage }) => (
    <div className={styles.card} style={{ padding: '0rem', background: 'transparent', border: 'none', boxShadow: 'none' }}>
        <div style={{
            width: '100%',
            height: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            textShadow: bannerImage ? '2px 2px 4px rgba(0,0,0,0.7)' : 'none'
        }}>
            {bannerImage ? <img src={bannerImage} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} /> : null}
        </div>
    </div>
);

const BrandHeader = ({ kpiData, timePeriod, onTimePeriodChange, onDownloadExcel, isDownloading }) => {
    const shopifyNotConfigured = kpiData?.shopify_not_configured || false;
    
    return (
        <div
            className={styles.card}
            style={{
                flexDirection: 'row', // Override column to row
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            {/* Left: Brand Name + Plan Badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontWeight: 700, fontSize: '1.5rem', color: '#1e293b' }}>
                    {kpiData?.brand_name || 'Brand Name'}
                </span>
                <span style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 600, 
                    backgroundColor: '#f1f5f9', 
                    color: '#475569',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    <span style={{ color: '#94a3b8', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Your Plan</span>
                    <span style={{ color: '#0d9488' }}>|</span>
                    {kpiData?.subscription?.plan_name || 'Roots'}
                </span>
            </div>

            {/* Right: Filter + Download Excel Button + Bell Icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {/* Time Period Filter */}
                <select 
                    value={timePeriod} 
                    onChange={onTimePeriodChange} 
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: '#f9f9f9', fontSize: '0.95rem', fontWeight: 500 }}
                >
                    <option value="today">Today</option>
                    <option value="7_days">Last 7 Days</option>
                    <option value="30_days">Last 30 Days</option>
                    <option value="3_months">Last 3 Months</option>
                    <option value="6_months">Last 6 Months</option>
                    <option value="1_year">Last 1 Year</option>
                    <option value="all_time">All Time</option>
                </select>
                <button
                    onClick={onDownloadExcel}
                    disabled={isDownloading || shopifyNotConfigured}
                    title={shopifyNotConfigured ? "Shopify not configured" : "Download Excel"}
                    style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: '1px solid #0d9488',
                        backgroundColor: shopifyNotConfigured ? '#f1f5f9' : '#ffffff',
                        color: shopifyNotConfigured ? '#94a3b8' : '#0d9488',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        cursor: shopifyNotConfigured ? 'not-allowed' : (isDownloading ? 'wait' : 'pointer'),
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        opacity: shopifyNotConfigured ? 0.6 : 1
                    }}
                >
                    {isDownloading ? (
                        <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                    ) : (
                        <span>📥</span>
                    )}
                    {isDownloading ? 'Downloading...' : 'Download Excel'}
                </button>
            </div>
        </div>
    );
};

// --- Main App Component ---
export default function BrandDashboard() {
    // Load Inter font (existing)
    useEffect(() => {
        const fontLink = document.createElement('link');
        fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
        fontLink.rel = 'stylesheet';
        fontLink.id = 'inter-font-link';
        if (!document.getElementById(fontLink.id)) {
            document.head.appendChild(fontLink);
        }

        document.body.style.fontFamily = "'Inter', sans-serif";

        return () => {
            const font = document.getElementById(fontLink.id);
            if (font) document.head.removeChild(font);
        };
    }, []);

    // Shopify KPI fetch
    const [kpiData, setKpiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bannerImages, setBannerImages] = useState(null);
    const [timePeriod, setTimePeriod] = useState('1_year');
    const [isDownloading, setIsDownloading] = useState(false);
    
    const handleTimePeriodChange = (e) => {
        setTimePeriod(e.target.value);
    };

    const handleDownloadExcel = async () => {
        if (kpiData?.shopify_not_configured) return;
        
        setIsDownloading(true);
        try {
            const token = localStorage.getItem('token') || process.env.REACT_APP_BRAND_DASH_TOKEN || window.__BRAND_DASH_TOKEN;
            const headers = {};
            if (token) headers['Authorization'] = `Bearer ${token}`;
            
            const downloadUrl = `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/shopify-kpi/export/?time_period=${timePeriod}`;
            
            const response = await fetch(downloadUrl, {
                method: 'GET',
                headers
            });
            
            if (!response.ok) {
                throw new Error('Download failed');
            }
            
            const blob = await response.blob();
            
            const disposition = response.headers.get('content-disposition');
            const filename = disposition?.match(/filename="([^"]+)"/)?.[1] 
                ?? `Dashboard_${timePeriod}_${new Date().toLocaleDateString()}.xlsx`;
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
            alert('Failed to download Excel file. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };
    
    // Update global KPI data only when kpiData changes


    useEffect(() => {
        let isMounted = true;
        async function load() {
            if (isMounted) {
                setLoading(true);
                setError(null);
            }
            try {
                const localToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
                const envToken = process.env.REACT_APP_BRAND_DASH_TOKEN;
                const globalToken = typeof window !== 'undefined' ? window.__BRAND_DASH_TOKEN : null;
                const token = localToken || globalToken || envToken;

                const headers = { 'Content-Type': 'application/json' };
                if (token) headers['Authorization'] = `Bearer ${token}`;

                const kpiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/shopify-kpi/?time_period=${timePeriod}`;
                const bannersUrl = `${process.env.REACT_APP_API_BASE_URL}/api/dashboard/banner-images/`;

                const [kpiRes, bannersRes] = await Promise.allSettled([
                    fetch(kpiUrl, { method: 'GET', headers }),
                    fetch(bannersUrl, { method: 'GET' })
                ]);

                // Handle KPI response
                if (kpiRes.status === 'fulfilled') {
                    if (kpiRes.value.ok) {
                        const data = await kpiRes.value.json();
                        setKpiDataGlobally(data);
                        console.log("KPI DATA IN DASHBOARD", data);
                        if (isMounted) {
                            setKpiData(data);
                            setError(null); // Clear any previous error
                        }
                    } else {
                        // Handle different error status codes - show appropriate error messages
                        const statusCode = kpiRes.value.status;
                        let errorMessage = '';
                        
                        if (statusCode >= 500) {
                            errorMessage = 'Unable to load KPI data due to a server error. Please try again later.';
                        } else {
                            errorMessage = 'Unable to load KPI data. Please refresh the page or try again.';
                        }
                        
                        if (isMounted) {
                            // Keeps the last figures on screen; blanking them
                            // reads as "your numbers are zero" rather than
                            // "we could not refresh them".
                            setError(errorMessage);
                        }
                    }
                } else {
                    // If API fails due to network error or other reasons
                    if (isMounted) {
                        setError(classifyError(new TypeError('Failed to fetch')));
                    }
                }

                // Handle Banner Images response (public, best-effort)
                if (bannersRes.status === 'fulfilled' && bannersRes.value.ok) {
                    const images = await bannersRes.value.json();
                    console.log('Banner Images:', images);
                    if (isMounted) setBannerImages(images);
                } else {
                    if (isMounted) setBannerImages(null);
                }
            } catch (e) {
                if (isMounted) {
                    setError(e && e.classified ? e.classified : classifyError(e));
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        }
        load();
        return () => { isMounted = false; };
    }, [timePeriod]); // Add timePeriod as dependency to re-fetch when it changes

    return (
        <>
            <div className={styles.dashboardPage}>
                <div className={styles.maxWidthContainer}>
                    <FeatureHeader
                        src="/images/feature-headers/brand-dashboard.png"
                        alt="Suspire Brand Dashboard"
                        style={{ marginBottom: '1.25rem' }}
                    />
                    <div style={{ display: 'grid', gap: '1rem', marginBottom: '1rem' }}>
                        <BrandHeader
                            kpiData={kpiData} 
                            timePeriod={timePeriod} 
                            onTimePeriodChange={handleTimePeriodChange}
                            onDownloadExcel={handleDownloadExcel}
                            isDownloading={isDownloading}
                        />
                        <OnboardingBannerPlaceholder bannerImage={bannerImages?.top} />
                    </div>

                    <main className={styles.mainGrid}>
                        {/*
                          Only when there are no figures to fall back on. With
                          data still on screen the dashboard keeps rendering it
                          and the notice above says how old it is.
                        */}
                        {error && !loading && !kpiData ? (
                            <DataFallback
                                variant="card"
                                error={typeof error === 'string' ? { message: error, retryable: true } : error}
                                onRetry={() => window.location.reload()}
                            />
                        ) : (
                            <>
                                {/* Brand Snapshot should span full width */}
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <BrandSnapshot data={kpiData} loading={loading} />
                                </div>

                                {/* LiveListingsHealth and VisibilityTracker side by side in a single full-width row */}
                                <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                            <LiveListingsHealth data={kpiData} loading={loading} />
                                            <VisibilityTracker data={kpiData} />
                                        </div>
                                    </div>
                                </div>

                                <div style={{ gridColumn: '1 / -1' }}>
                                    <CommunitySnapshot data={kpiData} loading={loading} />
                                </div>

                                {/* Recycled packaging banner should also span full width */}
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <OnboardingBannerPlaceholder bannerImage={bannerImages?.bottom} />
                                </div>
                            </>
                        )}
                    </main>
                </div>
            </div>
        </>
    )
}